import { invoiceUploadEvent } from '@/components/ReportForm/ApReportFormInputInvoiceFileField/hooks/event';
import { useCheckEventForAiOcrReflection } from '@/components/ReportForm/ApReportFormInputInvoiceFileField/hooks/useCheckEvent';
import { Invoice } from '@/components/ReportForm/ApReportFormInputInvoiceFileField/type';
import { useOcrInvoiceInferences } from '@/context/services/ocr-invoice-inferences/OcrInvoiceInferences.service';
import { OcrInferenceResult } from '@/features/InvoiceReport/Edit/components/type';
import { transformOcrApiResponse } from '@/features/InvoiceReport/Edit/components/utils/ocrDataTransform';
import { useTranslation } from '@/i18n';
import { isCommonError } from '@/libs/typeguard/isError';
import { useToastAPI } from '@moneyforward/ap-frontend-components';
import { useBooleanFlagValue } from '@openfeature/react-sdk';
import { useQueryClient } from '@tanstack/react-query';
import { CheckboxChangeEvent } from 'antd/es/checkbox';
import { getProcessOcrInvoiceInferencesQueryKey } from 'ap-openapi';
import {
  createContext,
  FC,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';

export type AiProgressType = 'ai_suggest' | 'ai_ocr' | 'no_assistance' | null;

interface OcrContextValue {
  isCheckedUseAiOcr: boolean;
  onChangeUseAiOcr: (e: CheckboxChangeEvent) => void;
  ocrInferenceResult: OcrInferenceResult | null;
  currentInvoiceFile: File | null;
  processInvoice: (invoice: Invoice) => Promise<void>;
  isProcessOcrInferenceLoading: boolean;
  isNewFileUpload: boolean;
  isOcrInferenceDrawerVisible: boolean;
  setOcrInferenceResult: (result: OcrInferenceResult | null) => void;
  isPaymentRequestAiSuggestionFlag: boolean;
  onChangeAiProgressType: (value: AiProgressType) => void;
  aiProgressType: AiProgressType;
}

const OcrContext = createContext<OcrContextValue | null>(null);

interface OcrProviderProps {
  children: ReactNode;
}

export const OcrProvider: FC<OcrProviderProps> = ({ children }) => {
  const isPaymentRequestAiSuggestionFlag = useBooleanFlagValue(
    'payment-request-ai-suggestion',
    false
  );

  const { checked: isCheckedUseAiOcr, onChange: onChangeUseAiOcr } =
    useCheckEventForAiOcrReflection(true);
  const { t } = useTranslation();
  const notify = useToastAPI();
  const queryClient = useQueryClient();

  const [currentInvoice, setCurrentInvoice] = useState<{
    file?: File;
    invoiceId?: string;
    is_used_ai_suggestion?: boolean;
  } | null>(null);
  const [ocrInferenceResult, setOcrInferenceResult] =
    useState<OcrInferenceResult | null>(null);
  const [currentInvoiceFile, setCurrentInvoiceFile] = useState<File | null>(
    null
  );
  const [isNewFileUpload, setIsNewFileUpload] = useState<boolean>(false);
  const [aiProgressType, setAiProgressType] =
    useState<AiProgressType>('ai_ocr');
  // Only call API when we have a valid invoice
  const queryResult = useOcrInvoiceInferences(
    currentInvoice?.file
      ? {
          file: currentInvoice.file,
          invoiceId: currentInvoice.invoiceId,
          is_used_ai_suggestion: currentInvoice.is_used_ai_suggestion,
        }
      : undefined
  );

  const { data, error, isLoading } = queryResult;

  const onChangeAiProgressType = useCallback((value: AiProgressType) => {
    setAiProgressType(value);
  }, []);

  const clearResult = useCallback(() => {
    setOcrInferenceResult(null);
    setCurrentInvoiceFile(null);
    setCurrentInvoice(null);
    setIsNewFileUpload(false);
  }, []);

  // Handle successful data - when we have valid data and a current invoice
  useEffect(() => {
    if (data && currentInvoice?.file && !isLoading) {
      try {
        const result = transformOcrApiResponse(data);
        setOcrInferenceResult(result);

        // For existing invoiceId cases, set the file to show in drawer
        if (currentInvoice.invoiceId && currentInvoice.file) {
          setCurrentInvoiceFile(currentInvoice.file);
        }
      } catch (err) {
        clearResult();
        notify.error({
          title: t('system_error'),
          duration: 3,
        });
      }
    }
  }, [
    data,
    currentInvoice?.file,
    currentInvoice?.invoiceId,
    clearResult,
    isLoading,
    notify,
    t,
  ]);

  // Handle errors - when we have an error and a current invoice
  useEffect(() => {
    if (error && currentInvoice?.file && !isLoading) {
      // Clear React Query cache for current invoice
      if (currentInvoice?.file) {
        const queryKey = getProcessOcrInvoiceInferencesQueryKey({
          file: currentInvoice.file,
          invoice_id: currentInvoice.invoiceId,
        });
        queryClient.removeQueries({ queryKey });
      }

      if (isCommonError(error)) {
        if (error?.code) {
          notify.error({
            title: error.messages?.join('</br>') || t('system_error'),
            duration: 3,
          });
        } else {
          notify.error({
            title: error.messages?.join('\n') || t('system_error'),
            duration: 3,
          });
        }
      } else {
        notify.error({
          title: error.message || t('system_error'),
          duration: 3,
        });
      }

      clearResult();
    }
  }, [
    error,
    currentInvoice?.file,
    currentInvoice?.invoiceId,
    isLoading,
    notify,
    t,
    queryClient,
    clearResult,
  ]);

  const getInvoiceFile = useCallback(
    (targetInvoice: Invoice) => {
      if (targetInvoice instanceof File) {
        return {
          file: targetInvoice,
          invoiceId: undefined,
          is_used_ai_suggestion: undefined,
        };
      }
      return {
        file: targetInvoice?.file,
        invoiceId: targetInvoice?.searchId,
        is_used_ai_suggestion:
          isPaymentRequestAiSuggestionFlag && aiProgressType === 'ai_suggest'
            ? true
            : undefined,
      };
    },
    [aiProgressType, isPaymentRequestAiSuggestionFlag]
  );

  const isOcrInferenceDrawerVisible = Boolean(ocrInferenceResult);

  const processInvoice = useCallback(
    async (invoice: Invoice) => {
      // Always trigger invoice upload event for form field updates first
      invoiceUploadEvent.Upload(invoice);

      // Determine if we should process OCR based on feature flag and settings
      const shouldProcessOcr = isPaymentRequestAiSuggestionFlag
        ? aiProgressType !== 'no_assistance'
        : isCheckedUseAiOcr;

      // Early return if OCR processing is not needed
      if (!shouldProcessOcr) {
        return;
      }

      // Determine is_used_ai_suggestion value
      const isUsedAiSuggestion = isPaymentRequestAiSuggestionFlag
        ? aiProgressType === 'ai_suggest'
        : undefined;

      const invoiceFile = getInvoiceFile(invoice);

      // Clear any previous query states first
      if (currentInvoice?.file) {
        const previousQueryKey = getProcessOcrInvoiceInferencesQueryKey({
          file: currentInvoice.file,
          invoice_id: currentInvoice.invoiceId,
          is_used_ai_suggestion: currentInvoice.is_used_ai_suggestion,
        });
        queryClient.removeQueries({ queryKey: previousQueryKey });
      }

      // Check if this is a new file upload (when invoiceId does not exist)
      const isNewFileUpload = !invoiceFile.invoiceId;

      // Reset all states
      clearResult();

      // Set the new file upload flag
      setIsNewFileUpload(isNewFileUpload);

      // Update current invoice file for drawer display if it's a new upload
      if (isNewFileUpload && invoiceFile.file) {
        setCurrentInvoiceFile(invoiceFile.file);
      }

      // Set the current invoice to trigger the query
      setCurrentInvoice({
        file: invoiceFile.file,
        invoiceId: invoiceFile.invoiceId,
        is_used_ai_suggestion: isUsedAiSuggestion,
      });
    },
    [
      isPaymentRequestAiSuggestionFlag,
      aiProgressType,
      getInvoiceFile,
      currentInvoice?.file,
      currentInvoice?.invoiceId,
      currentInvoice?.is_used_ai_suggestion,
      clearResult,
      queryClient,
      isCheckedUseAiOcr,
    ]
  );

  const contextValue: OcrContextValue = {
    isCheckedUseAiOcr,
    onChangeUseAiOcr,
    ocrInferenceResult,
    currentInvoiceFile,
    processInvoice,

    isProcessOcrInferenceLoading: isLoading && !!currentInvoice?.file,
    isNewFileUpload,
    isOcrInferenceDrawerVisible,
    setOcrInferenceResult,
    isPaymentRequestAiSuggestionFlag,
    onChangeAiProgressType,
    aiProgressType,
  };

  return (
    <OcrContext.Provider value={contextValue}>{children}</OcrContext.Provider>
  );
};

export const useOcrContext = (): OcrContextValue => {
  const context = useContext(OcrContext);
  if (!context) {
    throw new Error('useOcr must be used within an OcrProvider');
  }
  return context;
};
